import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";

// ── Shared types (mirror Prisma schema) ────────────────────────────────

export interface OrefCity {
  value: string;
  label: string;
  areaCode: number | null;
  areaNaturalLanguage: string | null;
  migunTime: number | null;
}

export interface LocationGroup {
  id: string;
  tenantId: string;
  name: string;
  status: "active" | "paused";
  cities: { cityValue: string; city: { value: string; label: string } }[];
  createdAt: string;
  updatedAt: string;
}

export interface AlertMessageLang {
  langCode: string;
  langLabel: string;
  langFlag: string;
  messageText: string;
  bannerUrl: string;
}

export interface AlertMessage {
  id: string;
  tenantId: string;
  name: string;
  category: string;
  bannerPath: string;
  bannerMime: string;
  addDatetime: boolean;
  enabled: boolean;
  sortOrder: number;
  langs: AlertMessageLang[];
  createdAt: string;
  updatedAt: string;
}

export interface OrefCardMapping {
  id: string;
  tenantId: string;
  orefTitle: string;
  cardId: string;
  enabled: boolean;
  sendHe: boolean;
  sendEn: boolean;
  locationAssignments: { type: "group" | "city"; refId: string }[];
}

export interface Member {
  id: string;
  userId: string;
  tenantId: string;
  role: "USER" | "TENANT_ADMIN" | "TENANT_SUPERADMIN";
  displayName: string | null;
  phone: string | null;
  mustChangePassword: boolean;
  createdAt: string;
  user: { id: string; email: string; name: string; platformRole: string; emailVerified: boolean };
}

// ── Hooks ───────────────────────────────────────────────────────────────

const base = (slug: string) => `/t/${slug}`;

// OREF cities catalog
export const useOrefCities = (slug: string | undefined, search?: string) =>
  useQuery({
    queryKey: ["tenant", slug, "oref-cities", search ?? ""] as const,
    queryFn: async () =>
      (
        await axios.get<OrefCity[], AxiosResponse<OrefCity[]>>(
          `${base(slug!)}/oref-cities`,
          { params: search ? { search } : {} },
        )
      ).data,
    enabled: !!slug,
  });

// Location groups
const lgKey = (slug: string) => ["tenant", slug, "location-groups"] as const;
export const useLocationGroups = (slug: string | undefined) =>
  useQuery({
    queryKey: lgKey(slug ?? ""),
    queryFn: async () =>
      (await axios.get<LocationGroup[], AxiosResponse<LocationGroup[]>>(`${base(slug!)}/location-groups`)).data,
    enabled: !!slug,
  });

export const useLocationGroupMutations = (slug: string | undefined) => {
  const qc = useQueryClient();
  const opts = { onSuccess: () => slug && qc.invalidateQueries({ queryKey: lgKey(slug) }) };
  return {
    create: useMutation({
      mutationFn: async (payload: { name: string; status?: "active" | "paused" }) =>
        (await axios.post(`${base(slug!)}/location-groups`, payload)).data,
      ...opts,
    }),
    update: useMutation({
      mutationFn: async (payload: { id: string; name?: string; status?: "active" | "paused" }) =>
        (await axios.patch(`${base(slug!)}/location-groups/${payload.id}`, payload)).data,
      ...opts,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => (await axios.delete(`${base(slug!)}/location-groups/${id}`)).data,
      ...opts,
    }),
    setCities: useMutation({
      mutationFn: async (payload: { id: string; cityValues: string[] }) =>
        (await axios.patch(`${base(slug!)}/location-groups/${payload.id}/cities`, {
          cityValues: payload.cityValues,
        })).data,
      ...opts,
    }),
  };
};

// Alert messages
const amKey = (slug: string) => ["tenant", slug, "alert-messages"] as const;
export const useAlertMessages = (slug: string | undefined) =>
  useQuery({
    queryKey: amKey(slug ?? ""),
    queryFn: async () =>
      (await axios.get<AlertMessage[], AxiosResponse<AlertMessage[]>>(`${base(slug!)}/alert-messages`)).data,
    enabled: !!slug,
  });

export const useAlertMessageMutations = (slug: string | undefined) => {
  const qc = useQueryClient();
  const opts = { onSuccess: () => slug && qc.invalidateQueries({ queryKey: amKey(slug) }) };
  return {
    create: useMutation({
      mutationFn: async (payload: Partial<AlertMessage> & { name: string }) =>
        (await axios.post(`${base(slug!)}/alert-messages`, payload)).data,
      ...opts,
    }),
    update: useMutation({
      mutationFn: async (payload: { id: string } & Partial<AlertMessage>) =>
        (await axios.patch(`${base(slug!)}/alert-messages/${payload.id}`, payload)).data,
      ...opts,
    }),
    remove: useMutation({
      mutationFn: async (id: string) =>
        (await axios.delete(`${base(slug!)}/alert-messages/${id}`)).data,
      ...opts,
    }),
    setLang: useMutation({
      mutationFn: async (payload: {
        id: string;
        langCode: string;
        langLabel?: string;
        langFlag?: string;
        messageText?: string;
        bannerUrl?: string;
      }) =>
        (
          await axios.post(
            `${base(slug!)}/alert-messages/${payload.id}/langs/${payload.langCode}`,
            payload,
          )
        ).data,
      ...opts,
    }),
    deleteLang: useMutation({
      mutationFn: async (payload: { id: string; langCode: string }) =>
        (
          await axios.delete(
            `${base(slug!)}/alert-messages/${payload.id}/langs/${payload.langCode}`,
          )
        ).data,
      ...opts,
    }),
  };
};

// OREF card mappings
const cmKey = (slug: string) => ["tenant", slug, "oref-card-mappings"] as const;
export const useCardMappings = (slug: string | undefined) =>
  useQuery({
    queryKey: cmKey(slug ?? ""),
    queryFn: async () =>
      (await axios.get<OrefCardMapping[], AxiosResponse<OrefCardMapping[]>>(
        `${base(slug!)}/oref-card-mappings`,
      )).data,
    enabled: !!slug,
  });

export const useCardMappingMutations = (slug: string | undefined) => {
  const qc = useQueryClient();
  const opts = { onSuccess: () => slug && qc.invalidateQueries({ queryKey: cmKey(slug) }) };
  return {
    update: useMutation({
      mutationFn: async (payload: { id: string } & Partial<OrefCardMapping>) =>
        (await axios.patch(`${base(slug!)}/oref-card-mappings/${payload.id}`, payload)).data,
      ...opts,
    }),
    setAssignments: useMutation({
      mutationFn: async (payload: { id: string; groupIds: string[]; cityValues: string[] }) =>
        (
          await axios.patch(`${base(slug!)}/oref-card-mappings/${payload.id}/assignments`, {
            groupIds: payload.groupIds,
            cityValues: payload.cityValues,
          })
        ).data,
      ...opts,
    }),
  };
};

// Members
const memKey = (slug: string) => ["tenant", slug, "members"] as const;
export const useMembers = (slug: string | undefined) =>
  useQuery({
    queryKey: memKey(slug ?? ""),
    queryFn: async () =>
      (await axios.get<Member[], AxiosResponse<Member[]>>(`${base(slug!)}/members`)).data,
    enabled: !!slug,
  });

export const useMemberMutations = (slug: string | undefined) => {
  const qc = useQueryClient();
  const opts = { onSuccess: () => slug && qc.invalidateQueries({ queryKey: memKey(slug) }) };
  return {
    invite: useMutation({
      mutationFn: async (payload: {
        email: string;
        role?: "USER" | "TENANT_ADMIN" | "TENANT_SUPERADMIN";
        displayName?: string;
        phone?: string;
      }) => (await axios.post(`${base(slug!)}/members`, payload)).data,
      ...opts,
    }),
    update: useMutation({
      mutationFn: async (payload: {
        membershipId: string;
        role?: "USER" | "TENANT_ADMIN" | "TENANT_SUPERADMIN";
        displayName?: string;
        phone?: string;
      }) =>
        (await axios.patch(`${base(slug!)}/members/${payload.membershipId}`, payload)).data,
      ...opts,
    }),
    remove: useMutation({
      mutationFn: async (membershipId: string) =>
        (await axios.delete(`${base(slug!)}/members/${membershipId}`)).data,
      ...opts,
    }),
  };
};
